import React, { useCallback, useMemo } from 'react'

import { Button, ButtonSize, ButtonVariant } from '@amzn/stencil-react-components/button'
import { InputFooter } from '@amzn/stencil-react-components/input'
import { Col, Row, Spacer, View } from '@amzn/stencil-react-components/layout'
import { useBreakpoints } from '@amzn/stencil-react-components/responsive'
import { Text } from '@amzn/stencil-react-components/text'
import { IconBinSmall as IconBin } from '@amzn/stencil-react-icons'

import { IconWithTooltip } from 'src/components/IconWithTooltip'
import { MediaManager } from 'src/components/MediaContentsEditor/MediaManager'
import { AvatarDTO } from 'src/models/dto/items/VirtualMeetingDTO'
import { Locale } from 'src/models/dto/Locale'
import { getListOfFileTypes, MediaFileGroups } from 'src/models/media/MediaTemplateDTO'
import { ItemEditorTextInput } from 'src/pages/module-builder/item-editors/ItemEditorInputs'
import { VirtualMeetingHandler } from 'src/services/EntityServices/ItemUpdateHandlers/VirtualMeetingHandler'

interface VirtualMeetingAttendeeItemProps {
    avatar: AvatarDTO
    avatarIndex: number
    itemId: string
    locale: Locale
    totalAttendees: number
    editDisabled?: boolean
    showValidationError?: boolean
    validationErrors: Record<string, string[]>
}

export const MIN_NUMBER_OF_ATTENDEES = 1
export const MAX_NUMBER_OF_ATTENDEES = 4

const VirtualMeetingAttendeeItemInner = ({
    avatar,
    avatarIndex,
    itemId,
    locale,
    totalAttendees,
    editDisabled,
    showValidationError,
    validationErrors,
}: VirtualMeetingAttendeeItemProps) => {
    const idPrefix = itemId
    const { matches } = useBreakpoints()
    const fieldFlex = matches.s ? '1 1' : '1 1 29%'

    const isSmallScreen = matches.s
    const attendeeLabelId = `${idPrefix}-attendee-${avatarIndex}`

    return (
        <View role='listitem' aria-labelledby={attendeeLabelId}>
            <Row
                alignItems='center'
                backgroundColor={isSmallScreen ? 'white' : 'neutral05'}
                justifyContent='space-between'
                padding={isSmallScreen ? 0 : 'S200'}
                width='100%'
            >
                <Text id={attendeeLabelId} color='neutral90'>{`Attendee ${avatarIndex + 1}`}</Text>
                {avatarIndex == 0 && <IconWithTooltip tooltipText={'Meeting organizer'} />}
                <Spacer flex='1 1 0' />
                {useMemo(
                    () =>
                        !isSmallScreen ? (
                            <Button
                                dataTestId={`avatar-${avatarIndex}-delete-button`}
                                icon={
                                    <IconBin title={`Delete meeting avatar ${avatarIndex + 1}`} />
                                }
                                aria-disabled={
                                    totalAttendees <= MIN_NUMBER_OF_ATTENDEES || editDisabled
                                }
                                variant={ButtonVariant.Tertiary}
                                size={ButtonSize.Small}
                                isDestructive
                                onClick={() => {
                                    VirtualMeetingHandler.deleteAvatar(itemId, avatarIndex)
                                }}
                            />
                        ) : undefined,
                    [avatarIndex, editDisabled, isSmallScreen, itemId, totalAttendees]
                )}
            </Row>
            <Row
                width='100%'
                gridGap='S300'
                alignItems={'flex-start'}
                flexWrap={'wrap'}
                padding={isSmallScreen ? 0 : 'S200'}
            >
                <Col flex={fieldFlex}>
                    {useMemo(
                        () => (
                            <ItemEditorTextInput
                                dataTestId={`avatar-${avatarIndex}-first-name`}
                                inputId={`first-name-${avatarIndex}`}
                                disabled={editDisabled}
                                value={avatar.firstNameI18N[locale] ?? ''}
                                placeholder={'E.g. Jane'}
                                itemId={itemId}
                                setValue={(nextValue: string) => {
                                    VirtualMeetingHandler.updateAvatarFirstName(
                                        itemId,
                                        locale,
                                        nextValue.trim(),
                                        avatarIndex
                                    )
                                }}
                                validationErrorMessage={(
                                    validationErrors[`avatar[${avatarIndex}].firstNameI18N`] ?? []
                                ).join(', ')}
                                showError={showValidationError}
                                labelText={'First name'}
                                maxCharacterLimit={12}
                            />
                        ),
                        [
                            avatar.firstNameI18N,
                            avatarIndex,
                            editDisabled,
                            itemId,
                            locale,
                            showValidationError,
                            validationErrors,
                        ]
                    )}
                </Col>
                <Col flex={fieldFlex}>
                    {useMemo(
                        () => (
                            <ItemEditorTextInput
                                dataTestId={`avatar-${avatarIndex}-last-name`}
                                inputId={`last-name-${avatarIndex}`}
                                disabled={editDisabled}
                                value={avatar.lastNameI18N[locale] ?? ''}
                                placeholder={'E.g. Smith'}
                                itemId={itemId}
                                setValue={(nextValue: string) => {
                                    VirtualMeetingHandler.updateAvatarLastName(
                                        itemId,
                                        locale,
                                        nextValue.trim(),
                                        avatarIndex
                                    )
                                }}
                                validationErrorMessage={(
                                    validationErrors[`avatar[${avatarIndex}].lastNameI18N`] ?? []
                                ).join(', ')}
                                showError={showValidationError}
                                labelText={'Last name'}
                                maxCharacterLimit={12}
                            />
                        ),
                        [
                            avatar.lastNameI18N,
                            avatarIndex,
                            editDisabled,
                            itemId,
                            locale,
                            showValidationError,
                            validationErrors,
                        ]
                    )}
                </Col>
                <Col flex={fieldFlex}>
                    {useMemo(
                        () => (
                            <ItemEditorTextInput
                                dataTestId={`avatar-${avatarIndex}-info-line-1`}
                                inputId={`info-line-1-${avatarIndex}`}
                                disabled={editDisabled}
                                value={avatar.informationLine1I18N[locale] ?? ''}
                                placeholder={'E.g. Product Manager'}
                                itemId={itemId}
                                setValue={(nextValue: string) => {
                                    VirtualMeetingHandler.updateAvatarInfoLine1(
                                        itemId,
                                        locale,
                                        nextValue.trim(),
                                        avatarIndex
                                    )
                                }}
                                validationErrorMessage={(
                                    validationErrors[
                                        `avatar[${avatarIndex}].informationLine1I18N`
                                    ] ?? []
                                ).join(', ')}
                                showError={showValidationError}
                                labelText={'Information line 1'}
                                maxCharacterLimit={24}
                            />
                        ),
                        [
                            avatar.informationLine1I18N,
                            avatarIndex,
                            editDisabled,
                            itemId,
                            locale,
                            showValidationError,
                            validationErrors,
                        ]
                    )}
                </Col>
                <Col flex={fieldFlex}>
                    {useMemo(
                        () => (
                            <ItemEditorTextInput
                                dataTestId={`avatar-${avatarIndex}-info-line-2`}
                                inputId={`info-line-2-${avatarIndex}`}
                                disabled={editDisabled}
                                value={avatar.informationLine2I18N[locale] ?? ''}
                                placeholder={'E.g. AWS'}
                                itemId={itemId}
                                setValue={(nextValue: string) => {
                                    VirtualMeetingHandler.updateAvatarInfoLine2(
                                        itemId,
                                        locale,
                                        nextValue.trim(),
                                        avatarIndex
                                    )
                                }}
                                validationErrorMessage={(
                                    validationErrors[
                                        `avatar[${avatarIndex}].informationLine2I18N`
                                    ] ?? []
                                ).join(', ')}
                                showError={showValidationError}
                                labelText={'Information line 2'}
                                maxCharacterLimit={24}
                            />
                        ),
                        [
                            avatar.informationLine2I18N,
                            avatarIndex,
                            editDisabled,
                            itemId,
                            locale,
                            showValidationError,
                            validationErrors,
                        ]
                    )}
                </Col>
                {!isSmallScreen && <Spacer flex='1 1 63%' />}
            </Row>
            <Col
                dataTestId={`avatar-${avatarIndex}-add-media`}
                padding={isSmallScreen ? 0 : 'S100'}
            >
                <Text color='neutral100' fontWeight='bold'>
                    Profile image
                </Text>
                <MediaManager
                    itemId={itemId}
                    locale={locale}
                    localeWiseMediaOpt={avatar.localeWiseMedia}
                    setLocaleWiseMediaOpt={useCallback(
                        (change) => {
                            VirtualMeetingHandler.updateAvatarLocaleWiseMediaOpt(
                                itemId,
                                change,
                                avatarIndex
                            )
                        },
                        [avatarIndex, itemId]
                    )}
                    disableMediaExpander={true}
                    maxNumberOfMedia={1}
                    buttonText={'Add profile image'}
                    buttonVariant={ButtonVariant.Secondary}
                    lockedFileTypes={getListOfFileTypes(MediaFileGroups.IMAGE)}
                    disabled={editDisabled}
                />
                {validationErrors[`avatar[${avatarIndex}].localeWiseMedia`] && (
                    <div>
                        <InputFooter
                            style={{ visibility: 'visible' }}
                            error
                            id={`avatar-${avatarIndex}-image-footer`}
                        >
                            {validationErrors[`avatar[${avatarIndex}].localeWiseMedia`].join(', ')}
                        </InputFooter>
                        <Spacer height='S400' />
                    </div>
                )}
            </Col>
            {useMemo(
                () =>
                    isSmallScreen ? (
                        <Col width={'100%'}>
                            <Button
                                dataTestId={`avatar-${avatarIndex}-delete-button`}
                                aria-disabled={
                                    totalAttendees <= MIN_NUMBER_OF_ATTENDEES || editDisabled
                                }
                                variant={ButtonVariant.Primary}
                                isDestructive
                                onClick={() => {
                                    VirtualMeetingHandler.deleteAvatar(itemId, avatarIndex)
                                }}
                            >
                                Delete Attendee {avatarIndex + 1}
                            </Button>
                            <Spacer height='S300' />
                        </Col>
                    ) : undefined,
                [avatarIndex, editDisabled, isSmallScreen, itemId, totalAttendees]
            )}
        </View>
    )
}

export const VirtualMeetingAttendeeItem = React.memo(VirtualMeetingAttendeeItemInner)
VirtualMeetingAttendeeItem.displayName = 'VirtualMeetingAttendeeItem'
